import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrophyIcon, 
  ClockIcon, 
  BoltIcon, 
  SparklesIcon,
  CheckCircleIcon,
  XMarkIcon,
  CubeIcon,
  RectangleStackIcon
} from '@heroicons/react/24/outline';
import { Card } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';

const SpeedMatchingGame = () => {
  const { user, updateUserStats } = useAuth();
  const isMechatronics = user?.profession === 'mechatronics';

  const programmerDict = [
    { de: 'Der Bildschirm', sq: 'Ekrani' },
    { de: 'Die Tastatur', sq: 'Tastiera' },
    { de: 'Die Maus', sq: 'Miu (Mouse)' },
    { de: 'Der Drucker', sq: 'Printeri' },
    { de: 'Das Kabel', sq: 'Kablli' },
    { de: 'Der Speicher', sq: 'Memoria' },
    { de: 'Die Datei', sq: 'Skedari/File' },
    { de: 'Das Passwort', sq: 'Fjalëkalimi' },
    { de: 'Das Netzwerk', sq: 'Rrjeti' },
    { de: 'Die Software', sq: 'Softueri' },
    { de: 'Die Hardware', sq: 'Hardueri' },
    { de: 'Die Nachricht', sq: 'Mesazhi' },
    { de: 'Der Fehler', sq: 'Gabimi' },
    { de: 'Die Hilfe', sq: 'Ndihma' },
    { de: 'Der Ordner', sq: 'Folderi' },
    { de: 'Die Schnittstelle', sq: 'Ndërfaqja/API' },
    { de: 'Die Variable', sq: 'Variabla' },
    { de: 'Die Schleife', sq: 'Loop/Cikli' },
    { de: 'Die Bedingung', sq: 'Kushti' },
    { de: 'Die Datenbank', sq: 'Baza e të dhënave' },
    { de: 'Der Quellcode', sq: 'Kodi burimor' },
    { de: 'Die Verschlüsselung', sq: 'Enkriptimi' },
    { de: 'Der Server', sq: 'Serveri' },
    { de: 'Die Abfrage', sq: 'Query/Kërkesa' },
    { de: 'Die Entwicklung', sq: 'Zhvillimi' },
    { de: 'Die Vererbung', sq: 'Trashëgimia' },
    { de: 'Die Anwendung', sq: 'Aplikacioni/Programi' },
    { de: 'Die Zuweisung', sq: 'Përcaktimi/Assignment' },
    { de: 'Die Ausführung', sq: 'Ekzekutimi' },
    { de: 'Die Fehlersuche', sq: 'Gjetja e gabimeve (Debugging)' }
  ];

  const mechatronicsDict = [
    { de: 'Der Lötkolben', sq: 'Maja e saldimit' },
    { de: 'Das Zahnrad', sq: 'Zupçiku' },
    { de: 'Der Messschieber', sq: 'Kallibëri' },
    { de: 'Die Zange', sq: 'Pinca' },
    { de: 'Der Hammer', sq: 'Çekiçi' },
    { de: 'Das Kugellager', sq: 'Kushineta' },
    { de: 'Die Drehbank', sq: 'Torni' },
    { de: 'Der Schraubendreher', sq: 'Kaçavida' },
    { de: 'Die Feile', sq: 'Lima' },
    { de: 'Das Schweißgerät', sq: 'Aparati i saldimit' },
    { de: 'Die Säge', sq: 'Sharra' },
    { de: 'Der Bohrer', sq: 'Trapani' },
    { de: 'Die Schutzbrille', sq: 'Syzet mbrojtëse' },
    { de: 'Der Stromkreis', sq: 'Qarku elektrik' },
    { de: 'Die Wartung', sq: 'Mirëmbajtja' },
    { de: 'Der Schaltplan', sq: 'Skema elektrike' },
    { de: 'Der Sensor', sq: 'Sensori' },
    { de: 'Der Aktor', sq: 'Aktori/Aktuatori' },
    { de: 'Das Relais', sq: 'Releu' },
    { de: 'Der Widerstand', sq: 'Rezistenca' },
    { de: 'Die Spannung', sq: 'Tensioni/Voltazhi' },
    { de: 'Die Frequenz', sq: 'Frekuenca' },
    { de: 'Der Kondensator', sq: 'Kondensatori' },
    { de: 'Die Spule', sq: 'Bobina' },
    { de: 'Der Motor', sq: 'Motori' },
    { de: 'Das Ventil', sq: 'Valvula/Ventili' },
    { de: 'Die Hydraulik', sq: 'Hidraulika' },
    { de: 'Die Pneumatik', sq: 'Pneumatika' },
    { de: 'Der Zylinder', sq: 'Cilindri' },
    { de: 'Das Messgerät', sq: 'Multimetri/Aparati matës' }
  ];

  const dictionary = isMechatronics ? mechatronicsDict : programmerDict;

  const [gameState, setGameState] = useState('start'); // start, playing, finished
  const [currentPair, setCurrentPair] = useState({ de: '', sq: '', isMatch: false });
  const [lastDe, setLastDe] = useState('');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [combo, setCombo] = useState(0);
  const [feedback, setFeedback] = useState(null); // 'correct', 'wrong'

  const generatePair = useCallback(() => {
    const isMatch = Math.random() > 0.5;
    let available = dictionary;
    if (lastDe) {
      available = dictionary.filter(d => d.de !== lastDe);
    }
    
    const item1 = available[Math.floor(Math.random() * available.length)];
    let item2;
    
    if (isMatch) {
      item2 = item1;
    } else {
      do {
        item2 = dictionary[Math.floor(Math.random() * dictionary.length)];
      } while (item2.de === item1.de);
    }

    setCurrentPair({ de: item1.de, sq: item2.sq, isMatch });
    setLastDe(item1.de);
    setFeedback(null);
  }, [dictionary, lastDe]);

  useEffect(() => {
    let timer;
    if (gameState === 'playing' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && gameState === 'playing') {
      const finish = async () => {
        const totalXp = (user?.xp || 0) + score;
        await updateUserStats({ xp: totalXp });
        setGameState('finished');
      };
      finish();
    }
    return () => clearInterval(timer);
  }, [gameState, timeLeft, score, user, updateUserStats]);

  const handleGuess = (guess) => {
    if (gameState !== 'playing' || feedback) return;

    if (guess === currentPair.isMatch) {
      setScore(prev => prev + 10 + (combo * 2));
      setCombo(prev => prev + 1);
      setFeedback('correct');
    } else {
      setCombo(0);
      setFeedback('wrong');
    }

    setTimeout(generatePair, 300);
  };

  const startGame = () => {
    setScore(0);
    setTimeLeft(30);
    setCombo(0);
    setGameState('playing');
    generatePair();
  };

  // -------------------------------------------------------------------------
  // THEMED UI: INVENTOR LIGHT vs TECH DARK
  // -------------------------------------------------------------------------
  
  if (gameState === 'start') {
    return (
      <Card className={`${isMechatronics ? '' : 'bg-zinc-950 text-white border-zinc-800'} p-12 text-center relative overflow-hidden`}>
        {isMechatronics ? (
          <div className="relative z-10">
            <div className="flex items-center justify-center gap-2 mb-6 opacity-30">
               <CubeIcon className="w-12 h-12 text-[#0696d7]" />
               <RectangleStackIcon className="w-12 h-12 text-[#f27d07]" />
            </div>
            <h2 className="text-3xl font-black text-brand-text mb-4 uppercase tracking-tighter italic">Blitz Match: Flashcards</h2>
            <p className="text-brand-text/60 mb-10 max-w-sm mx-auto font-bold text-xs uppercase tracking-widest">
              Analizo dy termat teknike. Nëse korrespondojnë, shtyp SAKTE. Shpejtësia është kyçe!
            </p>
            <button 
              onClick={startGame}
              className="px-16 py-4 bg-[#0696d7] text-white rounded-sm font-black text-xs shadow-lg hover:bg-[#057ab0] active:scale-95 transition-all uppercase tracking-[0.2em]"
            >
              Initialize Flash Test
            </button>
          </div>
        ) : (
          <div className="relative z-10">
            <SparklesIcon className="w-20 h-20 text-brand-primary/20 absolute -top-10 -left-10" />
            <h2 className="text-4xl font-black text-white mb-6 italic tracking-tighter">Blitz Match: Programmer</h2>
            <p className="text-brand-text/50 mb-10 max-w-sm mx-auto font-medium">
              A korrespondojnë fjalët e kodimit? Përgjigju sa më shpejt për të fituar Combo XP!
            </p>
            <button 
              onClick={startGame}
              className="px-16 py-6 bg-white text-black rounded-3xl font-black text-xl shadow-xl hover:scale-105 active:scale-95 transition-all"
            >
              START BATTLE ⚡
            </button>
          </div>
        )}
      </Card>
    );
  }

  if (gameState === 'finished') {
    return (
      <Card className={`${isMechatronics ? '' : 'bg-zinc-950 text-white border-zinc-800'} p-12 text-center`}>
        <TrophyIcon className={`w-24 h-24 ${isMechatronics ? 'text-[#f27d07]' : 'text-brand-primary'} mx-auto mb-6 animate-bounce`} />
        <h2 className={`text-4xl font-black ${isMechatronics ? 'text-brand-text' : 'text-white'} mb-2 italic uppercase`}>Session Terminated</h2>
        <div className={`text-6xl font-black ${isMechatronics ? 'text-[#0696d7]' : 'text-brand-primary'} mb-8`}>{score} XP</div>
        <button 
          onClick={startGame}
          className={`px-12 py-4 rounded-xl font-bold transition-all ${isMechatronics ? 'bg-gray-800 text-white hover:bg-black' : 'bg-white text-black hover:bg-brand-primary/10'}`}
        >
          Restart Session
        </button>
      </Card>
    );
  }

  return (
    <div className={`${isMechatronics ? 'bg-[#f5f5f5] border-[#bcbcbc]' : 'bg-black/90 border-white/5'} p-8 rounded-[2.5rem] border shadow-2xl font-sans relative overflow-hidden`}>
      
      {/* 2026 INVENTOR STYLE TOP BAR (if mechatronics) */}
      {isMechatronics && (
        <div className="absolute top-0 left-0 right-0 h-6 bg-[#e2e2e2] border-b border-[#bcbcbc] flex items-center px-4 justify-between">
           <span className="text-[8px] font-bold text-brand-text/60 grayscale opacity-60">FLASHCARD_ANALYZER_2026.iam</span>
           <div className="flex gap-2 text-[8px] text-brand-text/50"><span>-</span><span>□</span><span>×</span></div>
        </div>
      )}

      {/* HUD */}
      <div className="flex items-center justify-between mb-10 mt-4">
        <div className={`flex items-center gap-3 ${isMechatronics ? 'bg-white' : 'bg-white/5'} px-5 py-2 rounded-lg border ${isMechatronics ? 'border-brand-secondary/20' : 'border-white/10'}`}>
          <ClockIcon className={`w-5 h-5 ${timeLeft < 10 ? 'text-red-500 animate-pulse' : 'text-brand-accent'}`} />
          <span className={`text-xl font-black ${timeLeft < 10 ? 'text-red-500' : (isMechatronics ? 'text-brand-text' : 'text-white')}`}>{timeLeft}s</span>
        </div>
        
        <div className="flex flex-col items-end">
          <div className="text-[9px] font-black text-brand-text/50 uppercase tracking-widest mb-1">SCORE_BUFFER</div>
          <div className={`text-2xl font-black ${isMechatronics ? 'text-[#0696d7]' : 'text-brand-accent'}`}>{score}</div>
        </div>
      </div>

      {/* Combo Hub */}
      <div className="h-6 mb-4 flex justify-center">
        {combo > 1 && (
          <div className={`${isMechatronics ? 'bg-[#f27d07]' : 'bg-brand-primary'} text-primary-foreground px-3 py-0.5 rounded-sm text-[10px] font-black animate-bounce flex items-center gap-2 italic uppercase`}>
            <BoltIcon className="w-3 h-3" />
            COMBO MULTIPLIER X{combo}
          </div>
        )}
      </div>

      {/* Flashcard Card */}
      <div className={`
        ${isMechatronics ? 'bg-white border-[#bcbcbc]' : 'bg-gray-800 border-white/20'} 
        p-12 rounded-2xl shadow-xl border-2 transition-all duration-200 transform relative overflow-hidden
        ${feedback === 'correct' ? 'border-green-500 scale-[1.02]' : 
          feedback === 'wrong' ? 'border-red-500 shake' : ''}
      `}>
        {/* Grid pattern (if mechatronics) */}
        {isMechatronics && <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '15px 15px' }}></div>}
        
        <div className="flex flex-col items-center gap-8 relative z-10">
          <div className="text-center">
            <span className="text-[9px] font-black uppercase tracking-[0.3em] block mb-3 text-brand-accent">DEUTSCH</span>
            <div className={`text-3xl font-black italic tracking-tighter ${isMechatronics ? 'text-brand-text' : 'text-white'}`}>{currentPair.de}</div>
          </div>

          <div className={`w-10 h-10 ${isMechatronics ? 'bg-gray-100' : 'bg-white/5'} rounded-full flex items-center justify-center border ${isMechatronics ? 'border-brand-secondary/20' : 'border-white/10'}`}>
            <span className="text-brand-text/50 font-black text-[10px]">=</span>
          </div>

          <div className="text-center">
             <div className={`text-2xl font-black tracking-tight ${isMechatronics ? 'text-[#f27d07]' : 'text-brand-accent'}`}>{currentPair.sq}</div>
             <span className="text-[9px] font-black text-brand-text/50 uppercase tracking-[0.3em] block mt-3">ALBANISCH</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-4 mt-10">
        <button 
          onClick={() => handleGuess(false)}
          className={`p-6 rounded-xl border-2 transition-all active:scale-95 flex flex-col items-center shadow-lg group
            ${isMechatronics ? 'bg-white border-brand-secondary/20 hover:border-red-500' : 'bg-white/5 border-white/10 hover:border-red-500'}`}
        >
          <XMarkIcon className="w-10 h-10 text-gray-300 group-hover:text-red-500 mb-2 transition-colors" />
          <span className={`font-black uppercase text-[10px] tracking-widest ${isMechatronics ? 'text-brand-text/50' : 'text-brand-text/60'} group-hover:text-red-500`}>GABIM / INCORRECT</span>
        </button>

        <button 
          onClick={() => handleGuess(true)}
          className={`p-6 rounded-xl border-2 transition-all active:scale-95 flex flex-col items-center shadow-lg group
            ${isMechatronics ? 'bg-white border-brand-secondary/20 hover:border-green-500' : 'bg-white/5 border-white/10 hover:border-green-500'}`}
        >
          <CheckCircleIcon className="w-10 h-10 text-gray-300 group-hover:text-green-500 mb-2 transition-colors" />
          <span className={`font-black uppercase text-[10px] tracking-widest ${isMechatronics ? 'text-brand-text/50' : 'text-brand-text/60'} group-hover:text-green-500`}>REIHTIG / CORRECT</span>
        </button>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        .shake { animation: shake 0.2s ease-in-out 0s 2; }
      `}} />
    </div>
  );
};

export default SpeedMatchingGame;
